import { createFileRoute } from "@tanstack/react-router";
import { useSearch } from "@tanstack/react-router";
import { getLocationTypeLabel, isLocationTypeEnabled } from "api-types/locations/locationDisplay";
import type { LocationLevelType } from "api-types/locations/locationLevelTypes";
import { locationLevelTypeValues } from "api-types/locations/locationLevelTypes";
import type { ClientLocationBaseData } from "api-types/locations/locationsResponses";
import { FetchError } from "common/types/fetchError";
import { CreateLocation } from "lib/components/builders/communities/CreateLocation";
import { FetchErrorMessage } from "lib/components/errors/FetchErrorMessage";
import Page from "lib/components/layout/Page";
import { getBuilderLocationBaseDataURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import {
  errorPath,
  LOCATION_PARENT_PARAM,
  LOCATION_REDIRECT_PARAM,
  LOCATION_TYPE_PARAM,
  useAppRouterParams,
} from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import { useEffect, useMemo, useRef } from "react";
import Loading from "ui/components/Loading";
import { useAppNavigate } from "ui/hooks/useAppNavigate";
import withAuthentication from "../../../../components/withAuthentication";

function NewLocationPage() {
  const { builderId } = useAppRouterParams();
  const { builder, builderLoading } = useUserContext();
  const navigate = useAppNavigate();
  const searchParams = useSearch({ strict: false });
  const isRedirecting = useRef(false);
  const {
    [LOCATION_TYPE_PARAM]: locationType,
    [LOCATION_REDIRECT_PARAM]: redirectTo,
    [LOCATION_PARENT_PARAM]: parentId,
  } = searchParams as Record<string, string | undefined>;

  const fetchedData = useFetch<ClientLocationBaseData>(
    builderId && typeof parentId === "string" ? getBuilderLocationBaseDataURL({ builderId, locationId: parentId }) : "",
    [builderId, parentId],
    { handleErrorLocally: true },
  );
  const { data, loading, error } = fetchedData;

  // Validate location type synchronously during render
  const locationLevelName = useMemo<LocationLevelType | null>(() => {
    if (builderLoading || !builder) {
      return null;
    }

    if (typeof locationType !== "string" || !locationLevelTypeValues.includes(locationType as LocationLevelType)) {
      return null;
    }

    if (!isLocationTypeEnabled(locationType as LocationLevelType, builder.locationSettings)) {
      return null;
    }

    return locationType as LocationLevelType;
  }, [locationType, builder, builderLoading]);

  // Handle redirects for invalid location types
  useEffect(() => {
    if (builderLoading || !builder || isRedirecting.current) {
      return;
    }

    if (typeof locationType !== "string" || !locationLevelTypeValues.includes(locationType as LocationLevelType)) {
      isRedirecting.current = true;
      void navigate({ to: errorPath(400, undefined, "Invalid location type requested.") });
      return;
    }

    if (!isLocationTypeEnabled(locationType as LocationLevelType, builder.locationSettings)) {
      isRedirecting.current = true;
      void navigate({
        to: errorPath(403, undefined, "The requested location type is not enabled for your organization."),
      });
      return;
    }
  }, [locationType, builder, builderLoading, navigate]);

  if (!builderId) {
    return (
      <Page pageTitle="Error">
        <FetchErrorMessage fetchError={new FetchError(400, "Missing builder ID in route")} />
      </Page>
    );
  }

  // Show loading while builder loads, redirecting, or invalid location type
  if (builderLoading || !builder || isRedirecting.current || !locationLevelName) {
    return <Loading />;
  }

  const ready = !parentId || (!loading && !error && data);

  return (
    <Page pageTitle={`New ${getLocationTypeLabel(locationLevelName, builder.locationSettings)}`}>
      {!ready && loading && <Loading />}
      {!ready && error && <FetchErrorMessage fetchError={error} />}
      {ready && (
        <CreateLocation
          builderId={builderId}
          redirectTo={typeof redirectTo === "string" ? decodeURIComponent(redirectTo) : null}
          parentLocation={data?.location}
          locationLevelName={locationLevelName}
        />
      )}
    </Page>
  );
}

export const Route = createFileRoute("/builders/$builder_id/locations/new")({
  component: withAuthentication(NewLocationPage, {
    permissions: ["EDIT_COMMUNITIES"],
    resource: {
      resourceType: "communities",
      routeParamName: LOCATION_PARENT_PARAM,
    },
  }),
});
